import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { AdminService } from '../../services/adminService';
import { usePageTitle } from '../../hooks/usePageTitle';

const ITEMS_PER_PAGE = 20;

const AdminSenderManager: React.FC = () => {
  usePageTitle('Admin Sender Management');
  const { isDark } = useAdminTheme();
  const { showError } = useNotification();
  
  const [activeTab, setActiveTab] = useState<'domains' | 'emails'>('domains');
  const [showPending, setShowPending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [domains, setDomains] = useState<any[]>([]);
  const [emails, setEmails] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, showPending]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch each source independently to avoid one failure blocking everything
      const results = await Promise.allSettled([
        AdminService.getAllDomainVerifications(),
        AdminService.getAllSendingDomains(),
        AdminService.getAllEmailVerifications(),
        AdminService.getAllEmailMappings()
      ]);
      
      const domainsData = results[0].status === 'fulfilled' ? results[0].value : [];
      const sendingDomainsData = results[1].status === 'fulfilled' ? results[1].value : [];
      const emailsData = results[2].status === 'fulfilled' ? results[2].value : [];
      const emailMappingsData = results[3].status === 'fulfilled' ? results[3].value : [];

      if (results.some(r => r.status === 'rejected')) {
        console.warn('Some sender data sources failed to load:', results.filter(r => r.status === 'rejected'));
      }

      // Combine domain sources
      const combinedDomains = [...domainsData];
      sendingDomainsData.forEach((sd: any) => {
        if (!combinedDomains.find(d => d.id === sd.id)) {
          combinedDomains.push(sd);
        }
      });

      // Sort domains client-side by createdAt DESC
      combinedDomains.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });

      // Combine email sources
      const combinedEmails = [...emailsData];
      emailMappingsData.forEach((em: any) => {
        if (!combinedEmails.find(e => e.id === em.id)) {
          if (!em.status) em.status = 'active'; 
          combinedEmails.push(em);
        }
      });

      // Sort emails client-side by createdAt DESC
      combinedEmails.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });

      setDomains(combinedDomains);
      setEmails(combinedEmails);
    } catch (error: any) {
      console.error('Critical error in loadData:', error);
      showError('Failed to load sender data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'verified':
      case 'success':
      case 'active':
        return isDark ? 'bg-green-900/20 text-green-300' : 'bg-green-100 text-green-800';
      case 'pending':
      case 'warming':
        return isDark ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'expired':
        return isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-100 text-red-800';
      default:
        return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  const filteredData = (activeTab === 'domains' ? domains : emails).filter(item => {
    // Basic search filter
    const search = searchTerm.toLowerCase();
    const value = activeTab === 'domains' ? item.domain : item.email;
    const matchesSearch = (
      value?.toLowerCase().includes(search) ||
      item.userEmail?.toLowerCase().includes(search) ||
      item.userId?.toLowerCase().includes(search) ||
      item.workspaceName?.toLowerCase().includes(search) ||
      item.workspaceId?.toLowerCase().includes(search)
    );

    if (!matchesSearch) return false;

    // Status filter - include 'active' and 'warming' in the verified view
    // 'verified', 'success', 'active' are considered "Live"
    const status = item.status?.toLowerCase();
    const isVerified = status === 'verified' || status === 'success' || status === 'active';
    
    if (showPending) {
      // Show pending/warming/failed in pending tab
      // Include warming in the pending view too for management
      return !isVerified || status === 'warming'; 
    } else {
      // Show only truly active/verified in verified tab
      return isVerified;
    }
  });

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const verifiedDomainsCount = domains.filter(d => {
    const s = d.status?.toLowerCase();
    return s === 'verified' || s === 'success' || s === 'active';
  }).length;
  
  const verifiedEmailsCount = emails.filter(e => {
    const s = e.status?.toLowerCase();
    return s === 'verified' || s === 'success' || s === 'active';
  }).length;

  return (
    <div className="space-y-6">
      <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <div>
          <h2 className="text-2xl font-bold">Sender Management</h2>
          <div className="flex items-center space-x-4 mt-1">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              View and manage all domains and emails
            </p>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500">
              DB: {domains.length} domains / {emails.length} emails
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-4 w-full md:w-auto">
          <div className="relative flex-grow min-w-[300px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`block w-full pl-10 pr-3 py-2 border rounded-lg leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm transition-all duration-200 ${
                isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
              }`}
            />
          </div>
          <button
            onClick={loadData}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900 shadow-sm'
            }`}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <div className="flex">
          <button
            onClick={() => { setActiveTab('domains'); setSearchTerm(''); setShowPending(false); }}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'domains'
                ? 'border-cyan-500 text-cyan-500'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Domains ({verifiedDomainsCount})
          </button>
          <button
            onClick={() => { setActiveTab('emails'); setSearchTerm(''); setShowPending(false); }}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'emails'
                ? 'border-cyan-500 text-cyan-500'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Emails ({verifiedEmailsCount})
          </button>
        </div>

        <button
          onClick={() => setShowPending(!showPending)}
          className={`mr-4 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            showPending
              ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400'
              : 'text-gray-500 hover:text-cyan-500 dark:text-gray-400 dark:hover:text-cyan-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          {showPending ? '← Back to Verified' : `View Pending ${activeTab === 'domains' ? 'Domains' : 'Emails'}`}
        </button>
      </div>

      {/* Table */}
      <div className={`rounded-lg shadow overflow-hidden ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                  {activeTab === 'domains' ? 'Domain' : 'Email Address'}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                  Workspace
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                  User / Owner
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                  Status
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                  Created At
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {paginatedData.length > 0 ? (
                paginatedData.map((item) => (
                  <tr key={item.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className={`px-6 py-4 whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <div className="text-sm font-medium">{activeTab === 'domains' ? item.domain : item.email}</div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ID: {item.id}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      <div className="font-medium">{item.workspaceName}</div>
                      <div className="text-xs opacity-70">WID: {item.workspaceId}</div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      <div className="font-medium">{item.userEmail}</div>
                      <div className="text-xs opacity-70">UID: {item.userId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(item.status)}`}>
                        {item.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      {formatDate(item.createdAt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    {activeTab === 'domains' 
                      ? (showPending ? 'No pending domains found' : 'No verified domains found')
                      : `No emails found`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className={`px-6 py-4 border-t flex items-center justify-between ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Showing <span className="font-medium text-cyan-500">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-medium text-cyan-500">{Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length)}</span> of <span className="font-medium">{filteredData.length}</span> results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Previous
              </button>
              <div className="flex items-center">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show current page, first, last, and pages around current
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-md transition-colors ${
                          currentPage === page
                            ? 'bg-cyan-500 text-white'
                            : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSenderManager;
